// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, CountryModel, ShipViaDto, StateModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify";
import loadCartShippingInformation from "@tps/App/Handlers/Cart/LoadCartShippingInformation";
import setCartShippingInformation from "@tps/App/Handlers/Cart/SetCartShippingInformation";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { Warehouse } from "@tps/Overrides/Widgets/Cart/CartLines";
import { Carriers, SHIPPING_TERMS, ShippingTerms } from "@tps/XDL/constants";
import { color, modalButtonsActionStyles, xScreen, xValidateOnSubmit } from "@tps/XDL/Utilities";
import { maxLengthAttr } from "@tps/XDL/Utilities/validation";
import cloneDeep from "lodash/cloneDeep";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen?: boolean;
    setIsOpen: (isOpen: boolean) => void;
    setSelectedWarehouse: (warehouseId: string) => void;
    warehouse: Warehouse | { id: string; description: string };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        isAuthenticated: getSession(state).isAuthenticated,
        cart: cartState.value,
        showShippingMethod: session.fulfillmentMethod === FulfillmentMethod.Ship,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        shippingTerms: state.xShippingTerms.data?.values,
        countries: getCurrentCountries(state),
        shippingInfo: state.xShippingDetails.warehousesShippingDetails[props.warehouse.id],
        isLoadingShippingDetails: state.xShippingDetails.isLoading,
    };
};

const mapDispatchToProps = {
    loadCartShippingInformation,
    setRequestedDeliveryDate,
    setCartShippingInformation,
    loadCurrentCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface VerifyShippingDetailsModalStyles {
    buttonsWrapper?: InjectableCss;
    notifyMsg?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    shippingTermsSection?: InjectableCss;
    shippingMethodSection?: InjectableCss;
    shipViaSection?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    selectStyle?: SelectPresentationProps;
    upsMsg1?: TypographyPresentationProps;
    upsMsg2?: TypographyPresentationProps;
    carriersRadioGroup?: RadioGroupProps;
    carrierRadio?: RadioProps;
    upsAccountNumberField?: TextFieldPresentationProps;
    upsZipCodeField?: TextFieldPresentationProps;
    shippingTermsWrapper?: InjectableCss;
    cityStatePostalWrapper?: InjectableCss;
    cityWrapper?: InjectableCss;
    statePostalWrapper?: InjectableCss;
    cityText?: TextFieldPresentationProps;
    stateSelect?: SelectPresentationProps;
    postalCodeText?: TextFieldPresentationProps;
    sharedTextFieldStyles?: InjectableCss;
    errorMsg?: InjectableCss;
    modal?: ModalPresentationProps;
    shippingCommentField?: TextFieldPresentationProps;
    form?: InjectableCss;
}

export const verifyShippingDetailsModalStyles: VerifyShippingDetailsModalStyles = {
    buttonsWrapper: {
        ...modalButtonsActionStyles.buttonsGridItem,
        css: css`
            ${modalButtonsActionStyles.buttonsGridItem?.css}
            &&&& {
                margin-top: 0px;
                ${xScreen("gt", "lg")} {
                    margin-top: auto;
                }
            }
        `,
    },
    notifyMsg: {
        size: 12,
        lineHeight: "17px",
        forwardAs: "p",
        css: css`
            margin-bottom: 16px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            column-gap: 17px;
            width: 100%;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    shipViaSection: {
        css: css`
            min-width: 200px;
        `,
    },
    shippingMethodSection: {
        css: css`
            width: 196px;
            ${xScreen("lte", "md")} {
                width: 100%;
                margin-top: 24px;
            }
        `,
    },
    shippingTermsSection: {
        css: css`
            flex-grow: 1;
            ${xScreen("lte", "md")} {
                margin-top: 24px;
            }
        `,
    },
    sectionTitle: {
        transform: "uppercase",
        variant: "h6",
        forwardAs: "p",
        css: css`
            padding-bottom: 8px;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 16px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
        `,
    },
    selectStyle: {
        cssOverrides: {
            formInputWrapper: css`
                max-width: 196px;
                ${xScreen("lte", "md")} {
                    max-width: unset;
                }
            `,
        },
    },
    upsMsg1: {
        forwardAs: "p",
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-top: 16px;
        `,
    },
    upsMsg2: {
        forwardAs: "p",
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-top: 12px;
        `,
    },
    carriersRadioGroup: {
        css: css`
            & > legend {
                display: none;
            }
            &&&& > span {
                font-size: 14px;
                font-weight: normal;
                letter-spacing: 0;
                line-height: 22px;
                margin: 8px 0 0;
            }
        `,
    },
    carrierRadio: {
        css: css`
            display: flex;
        `,
    },
    upsAccountNumberField: {
        cssOverrides: {
            formField: css`
                width: 50%;
                margin-top: 16px;
                padding-right: 11px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                    padding-right: 0;
                }
            `,
        },
    },
    upsZipCodeField: {
        cssOverrides: {
            formField: css`
                width: 50%;
                margin-top: 16px;
                padding-right: 11px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                    padding-right: 0;
                }
            `,
        },
    },
    shippingTermsWrapper: {
        css: css`
            margin-top: 24px;
        `,
    },
    cityStatePostalWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "xs")} {
                gap: 16px;
            }
        `,
    },
    cityWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 50%;
            padding-right: 11px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                padding-right: 0;
            }
        `,
    },
    statePostalWrapper: {
        css: css`
            display: flex;
            gap: 16px;
            width: 50%;
            padding-left: 11px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                padding-left: 0;
            }
        `,
    },
    sharedTextFieldStyles: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    errorMsg: {
        css: css`
            margin-top: 16px;
        `,
    },
    modal: {
        cssOverrides: {
            modalBody: css`
                ${xScreen("lte", "md")} {
                    margin: 15px 140px;
                    min-width: 325px;
                }
            `,
        },
    },
    form: {
        css: css`
            ${xScreen("gt", "lg")} {
                min-height: 249px;
            }
            display: flex;
            flex-direction: column;
            row-gap: 24px;
        `,
    },
};

const styles = verifyShippingDetailsModalStyles;
const StyledForm = getStyledWrapper("form");

const VerifyShippingDetailsModal = ({
    isOpen,
    setIsOpen,
    warehouse,
    loadCartShippingInformation,
    setSelectedWarehouse,
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    showShippingMethod,
    shippingTerms,
    countries,
    setCartShippingInformation,
    shippingInfo,
    isLoadingShippingDetails,
    loadCurrentCart,
}: Props) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [selectedShippingTerm, setSelectedShippingTerm] = useState("");
    const [shippingTermIsRequired, setShippingTermIsRequired] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [accountNumber, setAccountNumber] = useState("");
    const [city, setCity] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [billToName, setBillToName] = useState("");
    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [loadErrorMessage, setLoadErrorMessage] = useState("");
    const [shippingComment, setShippingComment] = useState("");

    useEffect(() => {
        if (countries?.length === 1 && !selectedCountry) {
            setSelectedCountry(countries[0]);
        }
    }, []);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    useEffect(() => {
        if (shippingInfo) {
            setFormValues();
        }
    }, [shippingInfo]);
    const initShippingTerms = () => {
        setAccountNumber(shippingInfo?.accountNumber || "");
        setCity(shippingInfo?.city || "");
        setAddress1(shippingInfo?.address1 || "");
        setAddress2(shippingInfo?.address2 || "");
        setPostalCode(shippingInfo?.postalCode || "");
        setBillToName(shippingInfo?.billToName || "");
        setShippingComment(shippingInfo?.notes || "");
        let state = null;
        const country = countries && countries?.find(m => {
            const _state = m.states?.find(s => s.id === shippingInfo?.state);
            if (_state) {
                state = _state
            }
            return !!_state;
        });
        !!country && setSelectedCountry(country);
        !!state && setSelectedState(state);
    }
    const setFormValues = () => {
        const newCarrier = cart?.carriers?.find(c => c.id === shippingInfo?.shipViaId) || null;
        // TPS-555: sort shipping methods in alphabetical order
        const sortedShipVias = cloneDeep(newCarrier?.shipVias) || null;
        sortedShipVias &&
            sortedShipVias?.length > 1 &&
            sortedShipVias?.sort((shipVia1, shipVia2) => {
                const s1 = shipVia1.description.toUpperCase();
                const s2 = shipVia2.description.toUpperCase();

                if (s1 < s2) {
                    return -1;
                }
                if (s1 > s2) {
                    return 1;
                }
                return 0;
            });

        const newSelectedCarrier = sortedShipVias ? { ...newCarrier, shipVias: sortedShipVias } : newCarrier;
        setSelectedCarrier(newSelectedCarrier as any);
        setSelectedShipVia(
            newCarrier ? newCarrier.shipVias?.find(s => s.id === shippingInfo?.shippingMethod) || null : null,
        );
        setSelectedShippingTerm(shippingInfo?.termsType || "");
        initShippingTerms();
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        setSelectedCountry(country);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        setSelectedState(state);
    };
    const handleAccountNumberChange = (e: any) => {
        setAccountNumber(e.target.value);
    };
    const handleShippingCommentChange = (e: any) => {
        setShippingComment(e.target.value);
    };

    useEffect(() => {
        if (selectedCarrier && selectedCarrier.description.toLowerCase() !== Carriers.willCall) {
            setShippingTermIsRequired(true);
        } else {
            setShippingTermIsRequired(false);
        }
    }, [selectedCarrier]);

    useEffect(() => {
        setLoadErrorMessage("");
        if (!isLoadingShippingDetails && (!shippingInfo || !shippingInfo.shipViaId)) {
            loadCartShippingInformation({
                warehouseId: warehouse.id,
                onError: (error: string) => {
                    setLoadErrorMessage(error);
                },
            });
        }
    }, []);

    const selectedCarrierIsUps = selectedCarrier?.description.toLowerCase() === Carriers.ups;
    const selectedShippingTermIsCollect = selectedShippingTerm.toLowerCase() === ShippingTerms.collect;
    const selectedShippingTermIsThirdParty = selectedShippingTerm.toLowerCase() === ShippingTerms.thirdParty;

    const verifyShippingDetails = (event: React.FormEvent<HTMLFormElement>) => {
        setErrorMessage("");
        event.preventDefault();
        event.stopPropagation();
        setIsSubmitting(true);


        if (selectedShippingTermIsThirdParty || (selectedShippingTermIsCollect && selectedCarrierIsUps)) {
            const errors = xValidateOnSubmit(formId);
            if (errors.length > 0) {
                return;
            }
        }

        if (!selectedCarrier || !selectedShipVia || (shippingTermIsRequired && !selectedShippingTerm)) {
            return;
        }

        if (
            (selectedCarrier.description.toLowerCase() === Carriers.motorFreight || selectedCarrierIsUps) &&
            selectedShippingTermIsThirdParty &&
            (!selectedCountry || (selectedCountry?.states && selectedCountry?.states?.length > 0 && !selectedState))
        ) {
            return;
        }
        const data = {
            WarehouseId: warehouse.id,
            ShipViaId: selectedCarrier.id || "",
            ShippingMethod: selectedShipVia.id,
            TermsType: selectedShippingTerm,
            Notes: shippingComment,
            BillToName: selectedShippingTermIsThirdParty ? billToName : "",
            Address1: selectedShippingTermIsThirdParty ? address1 : "",
            Address2: selectedShippingTermIsThirdParty ? address2 : "",
            City: selectedShippingTermIsThirdParty ? city : "",
            State: selectedShippingTermIsThirdParty ? selectedState?.id : "",
            PostalCode: selectedShippingTermIsThirdParty || (selectedShippingTermIsCollect && selectedCarrierIsUps) ? postalCode : "",
            AccountNumber: selectedShippingTermIsThirdParty || (selectedShippingTermIsCollect && selectedCarrierIsUps) ? accountNumber : "",
        };

        setCartShippingInformation({
            shippingInfo: data,
            onSuccess: () => {
                setIsSubmitting(false);
                setIsOpen(false);
                setLoadErrorMessage("");
                loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
            },
            onError: (error: string) => {
                setIsSubmitting(false);
                setErrorMessage(error);
            },
        });
    };

    const handleCloseModal = () => {
        setIsOpen(false);
        setSelectedWarehouse("");
    };

    const carrierChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!cart || !cart.carriers || !cart.carriers.length) {
            return;
        }
        const selectedCarrier = cart.carriers.find(c => c.id === event.currentTarget.value)!;
        // TPS-555: sort shipping methods in alphabetical order
        const sortedShipVias = cloneDeep(selectedCarrier?.shipVias);
        sortedShipVias &&
            sortedShipVias?.length > 1 &&
            sortedShipVias?.sort((shipVia1, shipVia2) => {
                const s1 = shipVia1.description.toUpperCase();
                const s2 = shipVia2.description.toUpperCase();

                if (s1 < s2) {
                    return -1;
                }
                if (s1 > s2) {
                    return 1;
                }
                return 0;
            });

        const newcarrier = { ...selectedCarrier, shipVias: sortedShipVias };

        setSelectedCarrier(newcarrier);

        // When selected carrier is will call , then the defaul value for the dropdown shipvia is will call
        if (newcarrier.description.toLowerCase() === Carriers.willCall) {
            const willCallCarrier = newcarrier?.shipVias?.find(s => s.description.toLowerCase() === Carriers.willCall);

            setSelectedShipVia(willCallCarrier || null);
        } else {
            setSelectedShipVia(null);
        }

        setSelectedShippingTerm("");
    };

    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setSelectedShipVia(selectedShipVia);
    };

    const shippingTermChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedShippingTerm(event.currentTarget.value);
        initShippingTerms();
    };

    const getRequiredErrorMsg = (name: string) => {
        return `${translate("{0} is required", name)}`;
    };

    const clearShippingTermForm = () => {
        setAccountNumber("");
        setCity("");
        setAddress1("");
        setAddress2("");
        setPostalCode("");
        setBillToName("");
        setSelectedState(undefined);
    };

    const formId = "shippingDetailsModal";

    return (
        <>
            <Modal
                sizeVariant="large"
                headline={`${translate("Shipping from")} ${warehouse.description}`}
                isOpen={isOpen}
                handleClose={handleCloseModal}
                {...styles.modal}
            >
                {!cart ||
                    (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) ||
                    !cart.carriers ||
                    !countries ||
                    isLoadingShippingDetails ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
                    </StyledWrapper>
                ) : (
                    <>
                        <Typography {...styles.notifyMsg}>
                            {siteMessage("Custom_VerifyShippingDetailsModal_NotifyMsg")}
                        </Typography>
                        {showShippingMethod && (
                            <>
                                {cart.carriers!.length === 0 && (
                                    <StyledWrapper>
                                        <Typography>{siteMessage("ReviewAndPay_NoCarriersFound")}</Typography>
                                    </StyledWrapper>
                                )}
                                {cart.carriers!.length > 0 && (
                                    <>
                                        <StyledForm {...styles.form} onSubmit={verifyShippingDetails} noValidate>
                                            <StyledWrapper {...styles.wrapper}>
                                                <StyledWrapper {...styles.shipViaSection}>
                                                    <Typography {...styles.sectionTitle}>
                                                        {translate("Shipping via")}
                                                    </Typography>
                                                    <RadioGroup
                                                        {...styles.carriersRadioGroup}
                                                        data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                                        name="carrier"
                                                        value={selectedCarrier?.id || ""}
                                                        onChangeHandler={carrierChangeHandler}
                                                        required
                                                        error={
                                                            isSubmitting &&
                                                            !selectedCarrier &&
                                                            getRequiredErrorMsg(`${translate("Shipping via")}`)
                                                        }
                                                    >
                                                        {cart.carriers!.map(c => {
                                                            const id = c.id!.toString();
                                                            return (
                                                                <Radio {...styles.carrierRadio} key={id} value={id}>
                                                                    {c.description}
                                                                </Radio>
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </StyledWrapper>
                                                {selectedCarrier && (
                                                    <>
                                                        <StyledWrapper {...styles.shippingMethodSection}>
                                                            <Typography {...styles.sectionTitle}>
                                                                {translate("Shipping Method")}
                                                            </Typography>
                                                            <Select
                                                                value={selectedShipVia?.id || ""}
                                                                onChange={shipViaChangeHandler}
                                                                data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                                                {...styles.selectStyle}
                                                                required
                                                                error={
                                                                    isSubmitting &&
                                                                    !selectedShipVia &&
                                                                    getRequiredErrorMsg(
                                                                        `${translate("Shipping Method")}`,
                                                                    )
                                                                }
                                                            >
                                                                <option value="">{translate("Select Option")}</option>

                                                                {selectedCarrier &&
                                                                    selectedCarrier.shipVias &&
                                                                    selectedCarrier.shipVias?.map(s => {
                                                                        const id = s.id.toString();
                                                                        return (
                                                                            <option key={id} value={id}>
                                                                                {s.description}
                                                                            </option>
                                                                        );
                                                                    })}
                                                            </Select>
                                                            {selectedShipVia && selectedCarrierIsUps && (
                                                                <>
                                                                    <Typography {...styles.upsMsg1}>
                                                                        {siteMessage("Custom_UPS_Weight_Charge")}
                                                                    </Typography>
                                                                    <Typography {...styles.upsMsg2}>
                                                                        {siteMessage("Custom_Shipping_Weight_Message")}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </StyledWrapper>
                                                        {shippingTerms &&
                                                            selectedCarrier.description.toLowerCase() !==
                                                            Carriers.willCall && (
                                                                <StyledWrapper {...styles.shippingTermsSection}>
                                                                    <Typography {...styles.sectionTitle}>
                                                                        {translate("Shipping Terms")}
                                                                    </Typography>
                                                                    <Select
                                                                        value={selectedShippingTerm}
                                                                        onChange={shippingTermChangeHandler}
                                                                        data-test-selector="checkoutReviewAndSubmit_shippingTermsSelect"
                                                                        {...styles.selectStyle}
                                                                        required
                                                                        error={
                                                                            isSubmitting &&
                                                                            !selectedShippingTerm &&
                                                                            getRequiredErrorMsg(
                                                                                `${translate("Shipping Terms")}`,
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">
                                                                            {translate("Select Option")}
                                                                        </option>

                                                                        {shippingTerms!.map(s => {
                                                                            if (
                                                                                selectedCarrier.description.toLowerCase() ===
                                                                                Carriers.motorFreight &&
                                                                                !SHIPPING_TERMS[
                                                                                    Carriers.motorFreight
                                                                                ].terms.includes(
                                                                                    s.name?.toLowerCase() || "",
                                                                                )
                                                                            ) {
                                                                                return null;
                                                                            }
                                                                            return (
                                                                                <option key={s.name} value={s.name}>
                                                                                    {s.description}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </Select>
                                                                    {selectedShippingTerm && (
                                                                        <>
                                                                            {(selectedCarrier.description.toLowerCase() ===
                                                                                Carriers.motorFreight ||
                                                                                selectedCarrierIsUps) &&
                                                                                selectedShippingTerm.toLowerCase() ===
                                                                                ShippingTerms.thirdParty && (
                                                                                    <StyledWrapper
                                                                                        {...styles.shippingTermsWrapper}
                                                                                    >
                                                                                        <StyledWrapper
                                                                                            {...styles.sharedTextFieldStyles}
                                                                                        >
                                                                                            <TextField
                                                                                                id="billToName"
                                                                                                required
                                                                                                {...{ formId }}
                                                                                                value={billToName}
                                                                                                onChange={e =>
                                                                                                    setBillToName(
                                                                                                        e.currentTarget
                                                                                                            .value,
                                                                                                    )
                                                                                                }
                                                                                                placeholder={translate(
                                                                                                    "Third Party Bill to Name",
                                                                                                )}
                                                                                                {...{
                                                                                                    messageLabel: `${translate(
                                                                                                        "Bill to Name",
                                                                                                    )}`,
                                                                                                }}
                                                                                                data-test-selector="shippingTermsForm_billToName"
                                                                                                maxLength={
                                                                                                    maxLengthAttr.jobName
                                                                                                }
                                                                                                error={
                                                                                                    isSubmitting &&
                                                                                                    !billToName &&
                                                                                                    getRequiredErrorMsg(
                                                                                                        `${translate(
                                                                                                            "Bill to Name",
                                                                                                        )}`,
                                                                                                    )
                                                                                                }
                                                                                                type="text"
                                                                                            />
                                                                                        </StyledWrapper>

                                                                                        <StyledWrapper
                                                                                            {...styles.sharedTextFieldStyles}
                                                                                        >
                                                                                            <TextField
                                                                                                id="address1"
                                                                                                required
                                                                                                {...{ formId }}
                                                                                                value={address1}
                                                                                                onChange={e =>
                                                                                                    setAddress1(
                                                                                                        e.currentTarget
                                                                                                            .value,
                                                                                                    )
                                                                                                }
                                                                                                placeholder={translate(
                                                                                                    "Address Line 1",
                                                                                                )}
                                                                                                error={
                                                                                                    isSubmitting &&
                                                                                                    !address1 &&
                                                                                                    getRequiredErrorMsg(
                                                                                                        `${translate(
                                                                                                            "Address Line 1",
                                                                                                        )}`,
                                                                                                    )
                                                                                                }
                                                                                                {...{
                                                                                                    messageLabel: `${translate(
                                                                                                        "Address Line 1",
                                                                                                    )}`,
                                                                                                }}
                                                                                                data-test-selector="shippingTermsForm_address1"
                                                                                                maxLength={
                                                                                                    maxLengthAttr.address
                                                                                                }
                                                                                                type="text"
                                                                                            />
                                                                                        </StyledWrapper>

                                                                                        <StyledWrapper
                                                                                            {...styles.sharedTextFieldStyles}
                                                                                        >
                                                                                            <TextField
                                                                                                id="address2"
                                                                                                {...{ formId }}
                                                                                                value={address2}
                                                                                                onChange={e =>
                                                                                                    setAddress2(
                                                                                                        e.currentTarget
                                                                                                            .value,
                                                                                                    )
                                                                                                }
                                                                                                placeholder={translate(
                                                                                                    "Address Line 2",
                                                                                                )}
                                                                                                {...{
                                                                                                    messageLabel: `${translate(
                                                                                                        "Address Line 2",
                                                                                                    )}`,
                                                                                                }}
                                                                                                data-test-selector="shippingTermsForm_address2"
                                                                                                maxLength={
                                                                                                    maxLengthAttr.address
                                                                                                }
                                                                                                type="text"
                                                                                            />
                                                                                        </StyledWrapper>
                                                                                        <StyledWrapper
                                                                                            {...styles.cityStatePostalWrapper}
                                                                                        >
                                                                                            <StyledWrapper
                                                                                                {...styles.cityWrapper}
                                                                                            >
                                                                                                {countries.length >
                                                                                                    1 && (
                                                                                                        <Select
                                                                                                            value={
                                                                                                                selectedCountry?.id ||
                                                                                                                ""
                                                                                                            }
                                                                                                            required={true}
                                                                                                            onChange={
                                                                                                                handleCountryChange
                                                                                                            }
                                                                                                            error={
                                                                                                                isSubmitting &&
                                                                                                                !selectedCountry &&
                                                                                                                getRequiredErrorMsg(
                                                                                                                    `${translate(
                                                                                                                        "Country",
                                                                                                                    )}`,
                                                                                                                )
                                                                                                            }
                                                                                                            data-test-selector="shippingTermsForm_country"
                                                                                                        >
                                                                                                            <option value="">
                                                                                                                {translate(
                                                                                                                    "Select Country",
                                                                                                                )}
                                                                                                            </option>
                                                                                                            {countries.map(
                                                                                                                co => (
                                                                                                                    <option
                                                                                                                        key={
                                                                                                                            co.id
                                                                                                                        }
                                                                                                                        value={
                                                                                                                            co.id
                                                                                                                        }
                                                                                                                    >
                                                                                                                        {
                                                                                                                            co.name
                                                                                                                        }
                                                                                                                    </option>
                                                                                                                ),
                                                                                                            )}
                                                                                                        </Select>
                                                                                                    )}
                                                                                                <TextField
                                                                                                    {...styles.cityText}
                                                                                                    name="city"
                                                                                                    placeholder={translate(
                                                                                                        "City",
                                                                                                    )}
                                                                                                    value={city}
                                                                                                    required
                                                                                                    maxLength={
                                                                                                        maxLengthAttr.city
                                                                                                    }
                                                                                                    {...{ formId }}
                                                                                                    {...{
                                                                                                        messageLabel: `${translate(
                                                                                                            "City",
                                                                                                        )}`,
                                                                                                    }}
                                                                                                    error={
                                                                                                        isSubmitting &&
                                                                                                        !city &&
                                                                                                        getRequiredErrorMsg(
                                                                                                            `${translate(
                                                                                                                "City",
                                                                                                            )}`,
                                                                                                        )
                                                                                                    }
                                                                                                    onChange={e =>
                                                                                                        setCity(
                                                                                                            e
                                                                                                                .currentTarget
                                                                                                                .value,
                                                                                                        )
                                                                                                    }
                                                                                                    data-test-selector="shippingTermsForm_city"
                                                                                                />
                                                                                            </StyledWrapper>
                                                                                            <StyledWrapper
                                                                                                {...styles.statePostalWrapper}
                                                                                            >
                                                                                                {states.length > 0 && (
                                                                                                    <Select
                                                                                                        {...styles.stateSelect}
                                                                                                        value={
                                                                                                            selectedState?.id ||
                                                                                                            ""
                                                                                                        }
                                                                                                        required
                                                                                                        onChange={
                                                                                                            handleStateChange
                                                                                                        }
                                                                                                        error={
                                                                                                            isSubmitting &&
                                                                                                            !selectedState &&
                                                                                                            getRequiredErrorMsg(
                                                                                                                `${translate(
                                                                                                                    "State",
                                                                                                                )}`,
                                                                                                            )
                                                                                                        }
                                                                                                        data-test-selector="shippingTermsForm_state"
                                                                                                    >
                                                                                                        <option value="">
                                                                                                            {translate(
                                                                                                                "State",
                                                                                                            )}
                                                                                                        </option>
                                                                                                        {states.map(
                                                                                                            st => (
                                                                                                                <option
                                                                                                                    key={
                                                                                                                        st.id
                                                                                                                    }
                                                                                                                    value={
                                                                                                                        st.id
                                                                                                                    }
                                                                                                                >
                                                                                                                    {
                                                                                                                        st.abbreviation
                                                                                                                    }
                                                                                                                </option>
                                                                                                            ),
                                                                                                        )}
                                                                                                    </Select>
                                                                                                )}

                                                                                                <TextField
                                                                                                    {...styles.postalCodeText}
                                                                                                    name="postalCode"
                                                                                                    value={postalCode}
                                                                                                    placeholder={translate(
                                                                                                        "ZIP",
                                                                                                    )}
                                                                                                    required={true}
                                                                                                    maxLength={
                                                                                                        maxLengthAttr.postalCode
                                                                                                    }
                                                                                                    {...{ formId }}
                                                                                                    onChange={e =>
                                                                                                        setPostalCode(
                                                                                                            e
                                                                                                                .currentTarget
                                                                                                                .value,
                                                                                                        )
                                                                                                    }
                                                                                                    data-test-selector="shippingTermsForm_postalCode"
                                                                                                    {...{
                                                                                                        messageLabel: `${translate(
                                                                                                            "ZIP",
                                                                                                        )}`,
                                                                                                    }}
                                                                                                />
                                                                                            </StyledWrapper>
                                                                                        </StyledWrapper>
                                                                                    </StyledWrapper>
                                                                                )}
                                                                            {selectedCarrierIsUps &&
                                                                                (selectedShippingTerm.toLowerCase() ===
                                                                                    ShippingTerms.thirdParty ||
                                                                                    selectedShippingTermIsCollect) && (
                                                                                    <TextField
                                                                                        id="upsAccount#"
                                                                                        label={translate(
                                                                                            "UPS Account #",
                                                                                        )}
                                                                                        {...{
                                                                                            messageLabel: `${translate(
                                                                                                "UPS Account #",
                                                                                            )}`,
                                                                                        }}
                                                                                        type="text"
                                                                                        required
                                                                                        {...{ formId }}
                                                                                        value={accountNumber}
                                                                                        onChange={
                                                                                            handleAccountNumberChange
                                                                                        }
                                                                                        placeholder={translate(
                                                                                            "Account #",
                                                                                        )}
                                                                                        error={
                                                                                            isSubmitting &&
                                                                                            !accountNumber &&
                                                                                            getRequiredErrorMsg(
                                                                                                `${translate(
                                                                                                    "UPS Account #",
                                                                                                )}`,
                                                                                            )
                                                                                        }
                                                                                        {...styles.upsAccountNumberField}
                                                                                        data-test-selector="shippingTermsForm_accountNumber"
                                                                                    />
                                                                                )}
                                                                            {selectedCarrierIsUps &&
                                                                                selectedShippingTermIsCollect && (
                                                                                    <StyledWrapper>
                                                                                        <TextField
                                                                                            {...styles.upsZipCodeField}
                                                                                            name="postalCode"
                                                                                            value={postalCode}
                                                                                            label={translate(
                                                                                                "ZIP Code",
                                                                                            )}
                                                                                            placeholder={translate(
                                                                                                "ZIP",
                                                                                            )}
                                                                                            required={true}
                                                                                            maxLength={
                                                                                                maxLengthAttr.postalCode
                                                                                            }
                                                                                            {...{ formId }}
                                                                                            onChange={e =>
                                                                                                setPostalCode(
                                                                                                    e.currentTarget
                                                                                                        .value,
                                                                                                )
                                                                                            }
                                                                                            data-test-selector="shippingTermsForm_postalCode"
                                                                                            {...{
                                                                                                messageLabel: `${translate(
                                                                                                    "ZIP",
                                                                                                )}`,
                                                                                            }}
                                                                                        />
                                                                                    </StyledWrapper>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                </StyledWrapper>
                                                            )}
                                                    </>
                                                )}
                                            </StyledWrapper>
                                            {selectedCarrier && (
                                                <TextField
                                                    label={translate("Shipping Comment")}
                                                    type="notes"
                                                    value={shippingComment}
                                                    onChange={handleShippingCommentChange}
                                                    placeholder={translate("Comment")}
                                                    {...styles.shippingCommentField}
                                                    data-test-selector="shippingTermsForm_shippingComment"
                                                />
                                            )}
                                            {errorMessage && (
                                                <Notify {...styles.errorMsg} type="danger">
                                                    {errorMessage}
                                                </Notify>
                                            )}
                                            {loadErrorMessage && (
                                                <Notify {...styles.errorMsg} type="danger">
                                                    {loadErrorMessage}
                                                </Notify>
                                            )}
                                            <StyledWrapper {...styles.buttonsWrapper}>
                                                <Button
                                                    {...modalButtonsActionStyles.cancelButton}
                                                    onClick={handleCloseModal}
                                                    type="button"
                                                    aria-label="Cancel"
                                                >
                                                    {translate("Cancel")}
                                                </Button>
                                                <Button
                                                    {...modalButtonsActionStyles.submitButton}
                                                    type="submit"
                                                    aria-label="Verify"
                                                >
                                                    {translate("Verify")}
                                                </Button>
                                            </StyledWrapper>
                                        </StyledForm>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(VerifyShippingDetailsModal);
